'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Eye, EyeOff, Lock, Mail, AlertCircle, ArrowRight, ShieldCheck, GraduationCap, ArrowLeft, CheckCircle2, X } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

export default function StudentSignInClient() {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Forgot password state
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState(false);

  const supabase = createClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) {
        if (signInError.message.toLowerCase().includes('email not confirmed')) {
          setError('Please check your email and click the confirmation link before signing in. If you did not receive an email, contact support.');
        } else {
          setError('Invalid credentials. Please check your email and password.');
        }
        setLoading(false);
        return;
      }
      if (data.user) {
        // Check user_profiles role — also check auth metadata as fallback
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', data.user.id)
          .single();

        const metaRole = data.user.user_metadata?.role;
        const role = profile?.role || metaRole;

        if (!role || role === 'student') {
          // Ensure profile exists for newly registered students
          if (!profile) {
            await supabase.from('user_profiles').upsert({
              id: data.user.id,
              email: data.user.email,
              full_name: data.user.user_metadata?.full_name || '',
              role: 'student',
              phone: data.user.user_metadata?.phone || null,
            }, { onConflict: 'id' });
          }
          window.location.href = '/student-dashboard';
        } else {
          await supabase.auth.signOut();
          setError('Access denied. This portal is for students only.');
        }
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);
    try {
      const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://secure.myzivasol.com';
      // Use the server-side API route which generates the link via the Admin API.
      // This allows us to set a 1-hour expiry regardless of the dashboard OTP setting.
      const res = await fetch('/api/auth/send-reset-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: forgotEmail,
          redirectTo: `${siteUrl}/student-reset-password`,
        }),
      });
      const result = await res.json();
      if (!res.ok && result.error) {
        setForgotError(result.error);
      } else {
        setForgotSuccess(true);
      }
    } catch {
      setForgotError('An error occurred. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotModal = () => {
    setShowForgotModal(false);
    setForgotEmail('');
    setForgotError('');
    setForgotSuccess(false);
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 relative animate-fade-in">
            <button
              onClick={closeForgotModal}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X size={18} />
            </button>

            {forgotSuccess ? (
              <div className="flex flex-col items-center text-center py-4">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <CheckCircle2 size={32} className="text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Check Your Email</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  We've sent a password reset link to <strong>{forgotEmail}</strong>. Please check your inbox and follow the instructions.
                </p>
                <button onClick={closeForgotModal} className="btn-primary px-8">
                  Done
                </button>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    <Lock size={22} className="text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground">Forgot Password?</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Enter your email address and we'll send you a link to reset your password.
                  </p>
                </div>

                {forgotError && (
                  <div className="flex items-start gap-2.5 p-3.5 bg-red-50 border border-red-200 rounded-lg mb-4">
                    <AlertCircle size={15} className="text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{forgotError}</p>
                  </div>
                )}

                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <label className="form-label">Email Address</label>
                    <div className="relative">
                      <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="form-input pl-9"
                        placeholder="your@email.com"
                        required
                        autoFocus
                      />
                    </div>
                  </div>
                  <button type="submit" disabled={forgotLoading} className="btn-primary w-full py-2.5">
                    {forgotLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      'Send Reset Link'
                    )}
                  </button>
                  <button type="button" onClick={closeForgotModal} className="w-full py-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Cancel
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between w-[420px] xl:w-[480px] flex-shrink-0 bg-primary p-10 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-white/5" />
          <div className="absolute top-1/2 -left-16 w-48 h-48 rounded-full bg-white/5" />
          <div className="absolute -bottom-16 right-10 w-64 h-64 rounded-full bg-accent/10" />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-12 h-12 rounded-xl overflow-hidden bg-white/20">
              <Image src="/assets/images/logo-1778027352072.jpeg" alt="Ziva Niara Solutions logo" width={48} height={48} className="w-full h-full object-cover" />
            </div>
            <div>
              <span className="block text-lg font-bold text-white">Ziva DocuSecure</span>
              <span className="block text-xs text-white/60">Niara Solutions</span>
            </div>
          </div>
          <div className="mb-8">
            <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-5">
              <GraduationCap size={28} className="text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white leading-tight mb-3">Student Portal</h2>
            <p className="text-white/70 text-sm leading-relaxed">
              Sign in to access your biodata form, upload documents, track your submission status, and download your completed registration form.
            </p>
          </div>
          <div className="space-y-3">
            {[
              'Complete your biodata registration form',
              'Upload required documents securely',
              'Track document verification status',
              'Download your completed signed form',
              'Receive notifications and messages',
            ].map((f) => (
              <div key={f} className="flex items-center gap-2.5">
                <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                </div>
                <span className="text-sm text-white/80">{f}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10">
          <div className="h-px bg-white/20 mb-4" />
          <p className="text-xs text-white/50">docusecure.myzivasol.com · © 2026 Ziva Niara Solutions</p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10 overflow-y-auto">
        <div className="w-full max-w-md">
          <Link href="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors mb-8">
            <ArrowLeft size={14} />
            Back to Home
          </Link>

          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="w-9 h-9 rounded-lg overflow-hidden bg-primary/10">
              <Image src="/assets/images/logo-1778027352072.jpeg" alt="Ziva Niara Solutions logo" width={36} height={36} className="w-full h-full object-cover" />
            </div>
            <div>
              <span className="block text-sm font-bold text-primary">Ziva DocuSecure</span>
              <span className="block text-[10px] text-muted-foreground">Niara Solutions</span>
            </div>
          </div>

          <div className="mb-7">
            <h1 className="text-2xl font-bold text-foreground">Student Sign In</h1>
            <p className="text-sm text-muted-foreground mt-1">Access your student registration portal</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-start gap-2.5 p-3.5 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle size={15} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div>
              <label className="form-label">Email Address</label>
              <div className="relative">
                <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input pl-9"
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="form-label">Password</label>
              <div className="relative">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input pl-9 pr-10"
                  placeholder="Enter your password"
                  required
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-3.5 h-3.5 rounded accent-primary" />
                <span className="text-sm text-muted-foreground">Remember me</span>
              </label>
              <button
                type="button"
                onClick={() => setShowForgotModal(true)}
                className="text-sm text-primary font-semibold hover:underline"
              >
                Forgot password?
              </button>
            </div>

            <button type="submit" disabled={loading} className="btn-primary w-full py-2.5">
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">Sign In <ArrowRight size={15} /></span>
              )}
            </button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-5">
            New student?{' '}
            <Link href="/student-register" className="text-primary font-semibold hover:underline">Create an account</Link>
          </p>

          <div className="mt-5 flex items-start gap-2 p-3 bg-secondary/40 border border-primary/15 rounded-xl">
            <ShieldCheck size={14} className="text-primary flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Students can only access their own biodata form, uploaded documents, submission status, and messages.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
